<template>
  <div class="p-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="mb-0">Account removal requests</h4>
      <div>
        <b-button variant="outline-danger" class="mr-2" :disabled="isLoading || isSubmitting" @click="triggerFileSelect">
          <i class="fa fa-file-upload mr-1"></i> Upload CSV for removal
        </b-button>
        <input
          ref="csvFileInput"
          type="file"
          accept=".csv,text/csv"
          class="d-none"
          @change="onCsvFileSelected"
        />
        <b-button variant="primary" :disabled="isLoading" @click="refresh">
          <i class="fa fa-sync-alt mr-1"></i> Refresh
        </b-button>
      </div>
    </div>

    <b-table
      :items="requests"
      :fields="fields"
      striped
      hover
      responsive
      class="shadow-md rounded-lg"
      :busy="isLoading"
    >
      <template #cell(userEmail)="data">
        {{ (data.item.user && data.item.user.email) || '-' }}
      </template>
      <template #cell(createdBy)="data">
        {{ creatorName(data.item) }}
      </template>
      <template #cell(status)="data">
        <span :class="'badge ' + statusBadgeClass(data.item.status)">
          {{ data.item.status }}
        </span>
      </template>
      <template #cell(createdAt)="data">
        {{ data.item.createdAt | dateTime }}
      </template>
      <template #empty>
        <div class="text-center py-3">No removal requests yet.</div>
      </template>
    </b-table>

    <div v-if="totalRows > perPage" class="d-flex justify-content-center mt-3">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        :disabled="isLoading"
        align="center"
        @change="changePage"
      ></b-pagination>
    </div>

    <ConfirmCsvRemovalModal
      v-if="isCsvModalVisible"
      :emails="pendingEmails"
      :droppedCount="pendingDroppedCount"
      :isSubmitting="isSubmitting"
      :progress="submitProgress"
      @closeConfirmCsvRemovalModal="closeCsvModal"
      @confirmCsvRemoval="submitCsvRemoval"
    />
  </div>
</template>

<script>
import AccountRemovalAPI from '@/api/account-removal'
import ConfirmCsvRemovalModal from './ConfirmCsvRemovalModal.vue'
import { parseCsvEmails, chunkArray } from './parseCsvEmails'

const MAX_EMAILS_PER_REQUEST = 50

// The server caps a single call at 50, so a file turns into one sequential POST
// per 50 addresses. The run cannot be stopped once started and is not resumable,
// so cap what one upload may commit to rather than let a mis-picked file queue
// thousands of deletions over hundreds of requests. Splitting the file is the
// deliberate act that gets past this.
const MAX_EMAILS_PER_UPLOAD = 500

export default {
  name: 'RequestRemoval',
  components: {
    ConfirmCsvRemovalModal,
  },
  data() {
    return {
      requests: [],
      isLoading: false,
      isSubmitting: false,
      isCsvModalVisible: false,
      pendingEmails: [],
      pendingDroppedCount: 0,
      submitProgress: '',
      currentPage: 1,
      // Sent as the `limit` rather than left to the server default, so the page
      // size the pagination control divides by is always the one that was asked for.
      perPage: 50,
      totalRows: 0,
      fields: [
        { key: 'userId', label: 'User ID' },
        { key: 'userEmail', label: 'User email' },
        { key: 'createdBy', label: 'Created by' },
        { key: 'status', label: 'Status' },
        { key: 'createdAt', label: 'Created at' },
      ],
    }
  },
  created() {
    this.refresh()
  },
  methods: {
    async refresh() {
      this.isLoading = true
      try {
        const { removalRequests, total } = await AccountRemovalAPI.listRemovalRequests({
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          include: 'user,creator',
        })
        this.requests = removalRequests
        this.totalRows = total
      } catch (err) {
        console.error(err)
        this.$toasted.error((err && err.body && err.body.message) || 'Failed to load removal requests')
      } finally {
        this.isLoading = false
      }
    },
    // `change` fires before v-model writes back, so set the page here rather
    // than letting refresh() read whichever value lands first.
    changePage(page) {
      this.currentPage = page
      this.refresh()
    },
    // `creator` is null once the admin account is gone, so fall back to the raw
    // id rather than render blank/undefined.
    creatorName(item) {
      const creator = item.creator
      if (!creator) return item.createdBy || '-'
      const name = [creator.firstName, creator.lastName].filter(Boolean).join(' ')
      return name || creator.email || item.createdBy || '-'
    },
    statusBadgeClass(status) {
      if (status === 'completed') return 'badge-success'
      if (status === 'failed') return 'badge-danger'
      return 'badge-secondary'
    },
    triggerFileSelect() {
      this.$refs.csvFileInput.click()
    },
    onCsvFileSelected(event) {
      const file = event.target.files && event.target.files[0]
      // Clear immediately so selecting the same file twice still fires a change event.
      event.target.value = ''
      if (!file) return

      const reader = new FileReader()
      reader.onload = () => {
        const { emails, droppedCount } = parseCsvEmails(reader.result)
        if (emails.length === 0) {
          this.$toasted.error('No valid email addresses found in that file')
          return
        }
        if (emails.length > MAX_EMAILS_PER_UPLOAD) {
          this.$toasted.error(
            `That file holds ${emails.length} addresses; one upload may queue at most `
            + `${MAX_EMAILS_PER_UPLOAD}. Split it and upload the parts separately.`,
          )
          return
        }
        this.pendingEmails = emails
        this.pendingDroppedCount = droppedCount
        this.isCsvModalVisible = true
      }
      reader.onerror = () => {
        this.$toasted.error('Failed to read the CSV file')
      }
      reader.readAsText(file)
    },
    closeCsvModal() {
      this.isCsvModalVisible = false
      this.pendingEmails = []
      this.pendingDroppedCount = 0
      this.submitProgress = ''
    },
    // A rejected batch answers one of two shapes. A payload refused up front --
    // an address resolving to no account, or over the per-call cap -- answers
    // 400 { message }. A batch where every id failed to record answers 500
    // carrying the same { created, alreadyPending, failed } breakdown a 200
    // does, with no `message` at all. Reading only `message` would drop the
    // per-id reasons, which are the sole explanation the server gives.
    absorbBatchResult(body, batch, summary) {
      const result = body || {}
      if (Array.isArray(result.failed)) {
        summary.created += (result.created || []).length
        summary.alreadyPending += (result.alreadyPending || []).length
        summary.failed += result.failed.length
        result.failed.forEach((row) => summary.problems.push(`user ${row.userId}: ${row.reason}`))
        return
      }
      // No breakdown: the whole batch was refused, so none of its addresses landed.
      summary.rejected += batch.length
      summary.problems.push(result.message || `${batch.length} addresses rejected`)
    },
    async submitCsvRemoval() {
      this.isSubmitting = true
      const batches = chunkArray(this.pendingEmails, MAX_EMAILS_PER_REQUEST)
      const summary = {
        created: 0, alreadyPending: 0, failed: 0, rejected: 0, problems: [],
      }

      for (let i = 0; i < batches.length; i += 1) {
        this.submitProgress = `Batch ${i + 1} of ${batches.length}`
        try {
          const res = await AccountRemovalAPI.createRemovalRequests({ emails: batches[i] })
          this.absorbBatchResult(res, batches[i], summary)
        } catch (err) {
          console.error(err)
          // Carry on rather than abandon the rest. The server rejects a whole
          // batch for a single unknown address, so stopping at the first error
          // would silently skip every address after it -- most of the file, for
          // one stale entry -- while the toast named only what had been queued.
          this.absorbBatchResult(err && err.body, batches[i], summary)
        }
      }

      this.isSubmitting = false
      this.closeCsvModal()

      const parts = [`${summary.created} queued`]
      if (summary.alreadyPending > 0) parts.push(`${summary.alreadyPending} already pending`)
      // `failed` is a per-id insert error, not an unknown account -- an address
      // that resolves to nothing is refused up front and counted as rejected.
      // Calling these "not found" would have an admin drop addresses that exist
      // and still need retrying.
      if (summary.failed > 0) parts.push(`${summary.failed} failed`)
      if (summary.rejected > 0) parts.push(`${summary.rejected} rejected`)
      const summaryLine = `${parts.join(', ')} across ${batches.length} batch${batches.length === 1 ? '' : 'es'}`

      if (summary.problems.length === 0) {
        this.$toasted.success(summaryLine)
      } else {
        const shown = summary.problems.slice(0, 3).join('; ')
        const more = summary.problems.length > 3 ? ` (+${summary.problems.length - 3} more)` : ''
        this.$toasted.error(`${summaryLine}. ${shown}${more}`)
      }

      // New rows land at the head of a `createdAt DESC` list, so go back to the
      // first page -- refreshing in place would re-fetch whichever page the user
      // was on and show none of what they just queued.
      this.currentPage = 1
      this.refresh()
    },
  },
}
</script>
